<template>
  <FlexCol gap-3>
    <Grid cols-4 gap-3>
      <InputText v-model="object.id" label="Código" @keyup.enter="loadItems()" placeholder="Insira o código" />
      <Select :items="types" v-model="object.type" label="Tipo" placeholder="Selecione o tipo" />
      <InputZoom clearable v-model="object.idDestinationWarehouse" label="Local Destino" database="warehouse" item-text="name" item-value="id" @change="loadItems()" placeholder="Selecione o local de destino" />
      <InputDate clearable v-model="object.date" label="Data" @change="loadItems()" placeholder="Selecione a data" />
      <InputZoom
        clearable
        v-model="object.idItem"
        @update:modelValue="loadItems()"
        label="Material"
        database="item"
        item-value="code"
        item-text="name"
        @change="loadItems()"
        placeholder="Selecione o Material"
        :item-autocomplete="['code', 'name']" />
    </Grid>
    <TableFull :headers="stockHeaders" :items="itemWarehouse" v-model="control" @doubleClick="handleDoubleClick" dense>
      <template v-slot:date="{ item }">{{ formatFullDate(item.date) }}</template>
      <template v-slot:destinationName="{ item }">{{ item.type === 'supplierDevolution' ? item.supplierName : item.destinationName }}</template>
      <template v-slot:actions="{ item }">
        <Flex gap-2>
          <TheToolTip label="Editar">
            <TableIconButton icon="fa-pen" @click="navigate('movementUpdate', { id: item.idMovement }, true)" />
          </TheToolTip>
        </Flex>
      </template>
    </TableFull>
  </FlexCol>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { get } from '@/crud'
import { useNavigation } from '@/composables/useNavigation'
import { useFormat } from '@/composables/useFormat'
import moment from 'moment'

const { navigate } = useNavigation()
const { formatFullDate } = useFormat()
// Props
const props = defineProps({
  id: [String, Number],
  type: String,
})
//  Varibles
const api = 'itemMovement/searchAllMovementsByWarehouse'

const control = ref({
  total: 0,
  limit: 50,
  pages: 0,
  page: 1,
  order: [{ direction: 'DESC', column: 'date', name: 'Data' }],
})

const itemWarehouse = ref([])
const selectedItem = ref(null)

const stockHeaders = ref([
  { title: 'Código', field: 'idMovement', itemClass: 'w-1/12' },
  { title: 'Tipo', field: 'typeName', itemClass: 'w-2/12' },
  { title: 'Local Origem', field: 'originName', table: 'movement', orderField: 'idOriginWarehouse', itemClass: 'w-2/12' },
  { title: 'Local Destino', field: 'destinationName', table: 'movement', orderField: 'idDestinationWarehouse', itemClass: 'w-2/12' },
  { title: 'Data', field: 'date', itemClass: 'w-2/12' },
  { title: 'Observação', field: 'description', itemClass: 'w-2/12' },
  { title: 'Ações', field: 'actions', disableOrder: true, itemClass: 'w-1/12', headerClass: 'justify-center' },
])

const object = ref({})
const types = ref([])

const teamTypes = ref([
  { label: 'Fornecimento', value: 'outgoing' },
  { label: 'Devolução', value: 'devolution' },
  { label: 'Transferência', value: 'transfer' },
  { label: 'Baixa', value: 'downward' },
  { label: 'Aplicação', value: 'application' },
  { label: 'Fornecimento por Requisição', value: 'request' },
  { label: 'Correção de Baixa', value: 'downwardCorrection' },
  { label: 'Correção de Aplicação', value: 'applicationCorrection' },
  { label: 'Entrada de Inventário', value: 'inventoryEntry' },
  { label: 'Saída de Inventário', value: 'inventoryOut' },
])

const physicalTypes = ref([
  { label: 'Fornecimento', value: 'outgoing' },
  { label: 'Devolução', value: 'devolution' },
  { label: 'Devolução ao Fornecedor', value: 'supplierDevolution' },
  { label: 'Transferência', value: 'transfer' },
  { label: 'Fornecimento por Requisição', value: 'request' },
  { label: 'Entrada', value: 'entry' },
  { label: 'Entrada de Inventário', value: 'inventoryEntry' },
  { label: 'Saída de Inventário', value: 'inventoryOut' },
])

const vehicleTypes = ref([
  { label: 'Fornecimento', value: 'outgoing' },
  { label: 'Devolução', value: 'devolution' },
  { label: 'Transferência', value: 'transfer' },
  { label: 'Baixa', value: 'downward' },
  { label: 'Correção de Baixa', value: 'downwardCorrection' },
  { label: 'Entrada de Inventário', value: 'inventoryEntry' },
  { label: 'Saída de Inventário', value: 'inventoryOut' },
])

// Methods
const loadItems = async (page = 1) => {
  const filters = [
    {
      column: 'idWarehouse',
      value: props.id,
      operator: '=',
    },
  ]

  if (object.value.id) {
    filters.push({
      column: 'id',
      value: object.value.id,
      operator: '=',
    })
  }
  if (object.value.type) {
    filters.push({
      table: 'movement',
      column: 'type',
      value: object.value.type,
      operator: '=',
    })
  }
  if (object.value.idDestinationWarehouse) {
    filters.push({
      table: 'movement',
      column: 'idDestinationWarehouse',
      value: object.value.idDestinationWarehouse,
      operator: '=',
    })
  }
  if (object.value.date) {
    filters.push({
      table: 'movement',
      column: 'date',
      value: [moment(object.value.date).startOf('day').format(), moment(object.value.date).endOf('day').format()],
      operator: 'between',
    })
  }
  if (object.value.idItem) {
    filters.push({
      column: 'idItem',
      value: object.value.idItem,
      operator: '=',
    })
  }

  const query = {
    params: {
      page,
      limit: control.value.limit,
      order: control.value.order,
      filters,
    },
  }
  const raw = await get(api, query)
  itemWarehouse.value = raw.data
  control.value.pages = Math.ceil(raw.total / control.value.limit)
  control.value.total = raw.total
}

const handleDoubleClick = (item) => {
  navigate('movementUpdate', { id: item.idMovement }, true)
}

//  Watchers
watch(
  () => control.value,
  (newVal) => {
    loadItems(newVal.page || 1)
  },
  { deep: true },
)
watch(
  () => object.value,
  (newVal) => {
    loadItems()
  },
  { deep: true },
)

watch(selectedItem, () => {
  loadItems(1)
})

watch(
  () => props.type,
  (newType) => {
    if (newType === 'team') {
      types.value = teamTypes.value
    } else if (newType === 'physic') {
      types.value = physicalTypes.value
    } else if (newType === 'vehicles') {
      types.value = vehicleTypes.value
    }
  },
  { immediate: true },
)

// Life Cycle
onMounted(() => {
  loadItems()
})
</script>
